/** Persistent middle-distance meadow colonies, owned by intermediate terrain
 * tiles. Fine blades remain in the near band; these low mats survive its fade. */
import * as THREE from 'three';
import { rngFromString, type PlanetDef, type PlanetSampler, type TerrainSample } from '@hyperlight/shared';
import { faceDir } from '../cubesphere';
import { groundBeach, groundFertility, groundForest, groundMeadow, groundSnow, groundSavannaInland, groundColor, groundDuneSuppression, hasLivingGround } from '../groundSurface';
import { landscapePalette } from '../landscapePalette';
import { groundPatchAt } from '../groundDetail';
import { clearingAt } from './field';
import type { ClutterSets } from './clutter';
import { floraHeight, floraTime } from './material';
import { makePooledFloraMesh } from './materialPool';
import { prepareScatterBounds } from './scatterLod';
import {buildAlpineCover} from './alpineCover';
import {buildCanyonCover} from './canyonHabitat';
import {canyonCoverSuppression} from './canyonField';

type Kind='grass'|'shrub'|'flower';
type Tile={face:number;level:number;u0:number;v0:number;size:number};
export const MEADOW_LIMITS={grass:560,shrub:120,flower:72} as const;
const fresh=():TerrainSample=>({h:0,temp:0,moist:0,river:0,mountain:0,town:0,road:8,flowx:0,flowy:0,flowz:0});

export function meadowAllowed(ts:TerrainSample,def:PlanetDef,amplitude:number,footprint=0):boolean {
  const h=ts.h*amplitude;
  return hasLivingGround(def)&&ts.town<=.002&&ts.road>=1.8+footprint&&ts.river<.18
    &&(ts.riverWs??-1e9)<ts.h-.65/amplitude&&(ts.lavaDepthM??0)<=0&&(ts.glacierIceM??0)<.15
    &&(ts.wetlandWaterM??-1e9)<h-.2
    &&groundFertility(ts.temp,ts.moist)>.35&&ts.temp<.8
    &&groundSnow(ts.temp,h,def.climate.snowline*amplitude)<.12
    &&(def.seaRadius===null||groundBeach(def.radius+h-def.seaRadius)<.05);
}

export function buildMeadowCover(def:PlanetDef,sampler:PlanetSampler,clutter:ClutterSets,tile:Tile,
  clearingFreq:number,maturitySeed:number):THREE.InstancedMesh[] {
  if(!hasLivingGround(def))return buildCanyonCover(def,sampler,clutter,tile);
  const rng=rngFromString(`${def.seed}:meadow-cover:${tile.face}:${tile.level}:${tile.u0}:${tile.v0}`);
  const matrices:Record<Kind,THREE.Matrix4[]>={grass:[],shrub:[],flower:[]};
  const accepted:Record<Kind,number>={grass:0,shrub:0,flower:0};
  const evidence:Record<Kind,object[]>={grass:[],shrub:[],flower:[]};
  const grassPigments:([number,number,number,number]|null)[]=[];
  const ts=fresh(),probeTs=fresh(),dir:number[]=[];
  const up=new THREE.Vector3(),pos=new THREE.Vector3(),ex=new THREE.Vector3(),ez=new THREE.Vector3(),probe=new THREE.Vector3();
  const normal=new THREE.Vector3(),q=new THREE.Quaternion(),spin=new THREE.Quaternion(),scale=new THREE.Vector3(),matrix=new THREE.Matrix4(),Y=new THREE.Vector3(0,1,0),X=new THREE.Vector3(1,0,0);
  // Area of this cube-sphere patch, rather than latitude-dependent fixed counts.
  const midU=tile.u0+tile.size*.5,midV=tile.v0+tile.size*.5;
  const area=def.radius*def.radius*tile.size*tile.size/Math.pow(1+midU*midU+midV*midV,1.5);
  const pockets=Math.min(180,Math.round(area/48));
  const emit=(kind:Kind,u:number,v:number,size:number)=>{
    if(accepted[kind]>=MEADOW_LIMITS[kind]||u<tile.u0||u>=tile.u0+tile.size||v<tile.v0||v>=tile.v0+tile.size)return;
    faceDir(tile.face,u,v,dir);up.fromArray(dir);sampler.sample(up.x,up.y,up.z,ts,true);
    const footprint=size*(kind==='shrub'?1.1:kind==='grass'?.65:.3);
    if(!meadowAllowed(ts,def,sampler.amplitude,footprint))return;
    const radius=def.radius+ts.h*sampler.amplitude;
    const savanna=groundSavannaInland(ts.temp,ts.moist,radius-(def.seaRadius??-1e9));
    ex.crossVectors(up,Math.abs(up.y)<.9?Y:X).normalize();ez.crossVectors(up,ex);
    const heights:number[]=[];
    for(const [x,z] of [[1,0],[-1,0],[0,1],[0,-1]]){
      probe.copy(up).addScaledVector(ex,x*footprint/radius).addScaledVector(ez,z*footprint/radius).normalize();
      sampler.sample(probe.x,probe.y,probe.z,probeTs,true);
      if(!meadowAllowed(probeTs,def,sampler.amplitude))return;
      heights.push((probeTs.h-ts.h)*sampler.amplitude);
    }
    const gx=(heights[0]-heights[1])/(2*footprint),gz=(heights[2]-heights[3])/(2*footprint);
    const error=Math.max(Math.abs(heights[0]+heights[1]),Math.abs(heights[2]+heights[3]));
    if(Math.hypot(gx,gz)>.7||error>.16)return;
    normal.copy(up).addScaledVector(ex,-gx).addScaledVector(ez,-gz).normalize();
    q.setFromUnitVectors(Y,normal).multiply(spin.setFromAxisAngle(Y,rng()*Math.PI*2));
    pos.copy(up).multiplyScalar(radius-.025);
    // The template already reaches 1.1m before its 1.15–1.95 size factor.
    // A modest dry-sward lift keeps these broad tufts below standing eye level.
    scale.set(size,size*(kind==='grass'?.55+.10*savanna:1),size);matrix.compose(pos,q,scale);
    accepted[kind]++;
    const duneSuppression=groundDuneSuppression(ts.duneSand??0);
    if(duneSuppression>0&&rngFromString(`${def.seed}:dune-cover:${kind}:${u}:${v}`)()<duneSuppression)return;
    const canyonSuppression=canyonCoverSuppression(ts);
    if(canyonSuppression>0&&rngFromString(`${def.seed}:canyon-cover-replace:${kind}:${u}:${v}`)()<canyonSuppression)return;
    matrices[kind].push(matrix.clone());evidence[kind].push({x:pos.x,y:pos.y,z:pos.z,error,road:ts.road,town:ts.town,river:ts.river});
    if(kind==='grass'){
      const pigment=savanna>0?groundColor(landscapePalette(def),ts.temp,ts.moist,{heightM:ts.h*sampler.amplitude,
        seaLevelM:def.seaRadius===null?-1e9:def.seaRadius-def.radius,snowlineM:def.climate.snowline*sampler.amplitude,
        slope:1/Math.sqrt(1+gx*gx+gz*gz),river:ts.river,hasFlora:true,
        patch:groundPatchAt(pos.x,pos.y,pos.z),duneSand:ts.duneSand??0,butteRock:ts.butteRock??0,canyonRock:ts.canyonRock??0,canyonGround:ts.canyonGround??0},new THREE.Color()):null;
      grassPigments.push(pigment?[pigment.r,pigment.g,pigment.b,savanna]:null);
    }
  };
  for(let i=0;i<pockets;i++){
    const u=tile.u0+rng()*tile.size,v=tile.v0+rng()*tile.size;
    faceDir(tile.face,u,v,dir);sampler.sample(dir[0],dir[1],dir[2],ts,true);
    if(!meadowAllowed(ts,def,sampler.amplitude))continue;
    const r=def.radius+ts.h*sampler.amplitude;
    const patch=groundPatchAt(dir[0]*r,dir[1]*r,dir[2]*r);
    const clearing=clearingAt(dir[0],dir[1],dir[2],clearingFreq,maturitySeed^0x3517);
    const open=1-groundForest(ts.temp,ts.moist)*(1-Math.min(1,clearing*1.6));
    if(rng()>(.25+.75*groundMeadow(patch))*(.22+.78*open))continue;
    const spread=(2.2+rng()*2.6)/def.radius;
    const shrub=rng()<.68;
    for(let j=0;j<7;j++){
      const kind:Kind=j===0&&shrub?'shrub':j===6&&rng()<.48?'flower':'grass';
      emit(kind,u+(rng()-.5)*spread,v+(rng()-.5)*spread,kind==='shrub'?.85+rng()*.55:kind==='flower'?.9+rng()*.4:1.15+rng()*.8);
    }
  }
  const meshes:THREE.InstancedMesh[]=[];
  for(const kind of ['grass','shrub','flower'] as const){
    // Every originally occupied kind keeps its old variant draw even when
    // mobile sand filters its last instance, preserving the next kind's RNG.
    const variant=accepted[kind]>0?Math.floor(rng()*4):0;
    if(!matrices[kind].length)continue;
    const geo=clutter[kind][variant],fadeFar=kind==='shrub'?420:kind==='grass'?320:180;
    const mesh=makePooledFloraMesh(geo,matrices[kind].length,{birth:floraTime(),fadeFar,height:floraHeight(geo),rigid:false});
    matrices[kind].forEach((m,i)=>mesh.setMatrixAt(i,m));mesh.instanceMatrix.needsUpdate=true;
    if(kind==='grass'&&grassPigments.some(Boolean)){
      // Existing green templates retain their faceted variation. Per-instance
      // tint brings only warm dry grass to the same albedo as near blades/soil.
      const attr=geo.getAttribute('color'),mean=[0,0,0];
      for(let i=0;i<attr.count;i++){mean[0]+=attr.getX(i);mean[1]+=attr.getY(i);mean[2]+=attr.getZ(i);}
      for(let i=0;i<3;i++)mean[i]=Math.max(.001,mean[i]/attr.count);
      const tint=new THREE.Color();
      grassPigments.forEach((p,i)=>{mesh.setColorAt(i,p?tint.setRGB(1+(p[0]/mean[0]-1)*p[3],1+(p[1]/mean[1]-1)*p[3],1+(p[2]/mean[2]-1)*p[3]):tint.setRGB(1,1,1));});
      mesh.instanceColor!.needsUpdate=true;
    }
    mesh.userData={meadowCover:kind,fadeFar,coverEvidence:evidence[kind],scatterLodNear:100,scatterLodFar:135};
    mesh.castShadow=false;prepareScatterBounds(mesh,kind==='shrub'?clutter.shrubCoarse[variant]:undefined);
    mesh.receiveShadow=true;mesh.visible=false;meshes.push(mesh);
  }
  return meshes.concat(buildCanyonCover(def,sampler,clutter,tile),buildAlpineCover(def,sampler,clutter,tile));
}
